import React, {useEffect, useRef, useState} from 'react';
import { Alert } from '@curves/ui-base';
import clsx from 'clsx';
import {Dialog, DialogContent, Grid} from '@mui/material';
import {GradientColorHeader} from '@Local/Components/Dialog/';
import {EditableInfo} from '@Local/Components';
import {formatCurrencyFn, isFunction} from '@Util/utils';
import {parseDate} from '@Util/moment.util';
import RefundSettlement from '@Local/Orders/Componetnets/RefundSettlement/RefundSettlement';
import useOrderReturnApi from '@Apis/useOrderReturnApi';
import {DATE_TIME_STANDARD} from '@Constants/appConfig';
/**
 * 正規化訂單項目資料
 * @param {Object} row - 原始訂單項目資料
 * @returns {Object} 正規化後的訂單項目資料
 */
const normalizeLine = row => ({
    orderItemID: row.orderItemID,
    orderID: row.orderID,
    productType: row.productType,
    productTypeText: row.productTypeText || "",
    productName: row.productName || "",
    variantName: row.variantName || "",
    salePrice: Number(row.salePrice) || 0,
    quantity: Number(row.quantity) || 0,
    returnPrice: Number(row.returnPrice) || 0,
    returnQuantity: Number(row.returnQuantity) || 0,
    notes: row.notes || "",
    onlyAllowancePrice: Boolean(row.onlyAllowancePrice),
});
/**
 * 安全取得陣列資料
 * @param {Array} arr - 原始陣列
 * @returns {Array} 確保為陣列的資料
 */
const safeList = (arr) => Array.isArray(arr) ? arr : [];
/**
 * 訂單折讓彈窗：載入資料並提交折讓結果。
 */
const OrderAllowanceDialog = React.forwardRef(({open = true, sourceData, onClose, refresh}, ref) => {
    if (!open) return null;
    const {orderID} = sourceData || {};
    const {getOrderReturnAllowanceDialogApi, postOrderReturnAllowanceConfirmApi} = useOrderReturnApi();
    const [orderData, setOrderData] = useState({});
    const listDisabled = orderData?.invoiceReturnPrice === orderData?.invoiceTotalPrice;
    const refundSettlementRef = useRef()
    const fetchOrderReturnAllowanceDialog = async (orderID) => {
        try {
            const resp = await getOrderReturnAllowanceDialogApi(orderID);
            if (resp) {
                setOrderData(resp);
            }
        } catch (error) {
            console.error('取得訂單折讓資料失敗:', error);
        }
    }
    const doPostOrderReturnAllowanceConfirmApi = async (params) => {
        try {
            const resp = await postOrderReturnAllowanceConfirmApi(params);
            // 驗證失敗時 resp 仍是物件，不可簡化為 if (resp)，否則會誤判成功而關閉畫面
            if (resp && !resp.isError) {
                isFunction(refresh) && refresh();
                isFunction(onClose) && onClose();
            }
        } catch (error) {
            console.error('折讓失敗:', error);
        }
    }
    const handleSubmit = (params) => {
        const { isError } = refundSettlementRef.current || {};
        if (!isError()) {
            doPostOrderReturnAllowanceConfirmApi(params);
        }
    }
    useEffect(() => {
            if (!orderID || !open) return;
            fetchOrderReturnAllowanceDialog(orderID);
        },
        // eslint-disable-next-line
        [orderID, open]);
    return (
        <Dialog className={clsx('simple-content', 'dialog')}
                PaperProps={{className: 'wx-90rem'}} open={open} fullWidth>
            <GradientColorHeader title="訂單折讓" onClose={onClose}/>
            <DialogContent className={'container main-container-spacing bg-pager'}>
                <Grid className={'mb-2'} spacing={1} container>
                    <Grid xs={4} item>
                        <EditableInfo label={'訂單編號'}>{orderData?.orderNo}</EditableInfo>
                    </Grid>
                    <Grid xs={4} item>
                        <EditableInfo
                            label={'訂單日期'}>{parseDate(orderData?.orderDate, DATE_TIME_STANDARD.DISPLAYFULLBYSLASH)}</EditableInfo>
                    </Grid>
                    <Grid xs={4} item>
                        <EditableInfo label="發票號碼">{orderData?.invoiceNo || "無相關資料"}</EditableInfo>
                    </Grid>
                    <Grid xs={4} item>
                        <EditableInfo
                            label="發票金額">{formatCurrencyFn(orderData?.invoiceTotalPrice || 0, {symbol: '$'})}</EditableInfo>
                    </Grid>
                    <Grid xs={4} item>
                        <EditableInfo
                            label="退單金額">{formatCurrencyFn(orderData?.invoiceReturnPrice || 0, {symbol: '$'})}</EditableInfo>
                    </Grid>
                    {orderData?.isManual ? (
                        <Grid xs={12} item>
                            <Alert className="alert-justify-content-start" color="info" severity="info"
                                   variant="filled">
                                此為手開發票訂單，發票異動需由店舖自行處理。
                            </Alert>
                        </Grid>
                    ) : null}
                    <Grid xs={12} item>
                        <RefundSettlement
                            ref={refundSettlementRef}
                            groups={[
                                {
                                    key: "main",
                                    title: listDisabled ? "已無可折讓品項" : "請選擇要折讓的品項",
                                    items: safeList(orderData?.items).map(normalizeLine),
                                },
                            ]}
                            onSubmit={handleSubmit}
                            listDisabled={listDisabled}
                            isTriplicateUniformInvoice={orderData?.isTriplicateUniformInvoice}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
});
export default OrderAllowanceDialog;
